/**
 * 「この版と最新との比較」パネル (マスタ変更履歴 SBI 7)。
 *
 * 開いたときに初めて `diff` を呼ぶ (詳細を開いただけでは API を増やさない)。
 * 差分の計算はサーバ側で、ここは結果の表示だけを行う。
 *
 * 状態:
 *   - 読み込み中 / 取得失敗 (再読み込み付き)
 *   - 404: 比較元の版が見つからない
 *   - `is_latest`: この版が最新 (差分は空)
 *   - `deleted`: 現在は削除済み (差分は空)
 *   - 差なし / 差あり
 */
import { useMemo } from 'react';
import type React from 'react';

import { ApiError } from '../api/common';
import type { MasterHistoryClient } from '../api/masterHistory';
import type { MasterHistoryDiff } from '../api/types';
import { useMasterHistoryLoad } from '../hooks/useMasterHistoryLoad';
import { describeApiErrorMessage } from '../utils/apiErrorMessage';

import { MasterHistoryChangeTable } from './MasterHistoryChangeTable';
import { Spinner } from './Spinner';
import { StatusBanner } from './StatusBanner';

export const DIFF_NOT_FOUND_MESSAGE = '比較元の版が見つかりません';
export const DIFF_IS_LATEST_MESSAGE = 'この版が最新の版です。比較する差分はありません';
export const DIFF_DELETED_MESSAGE =
  'この対象は現在削除されているため、最新の内容とは比較できません';
export const DIFF_NO_CHANGES_MESSAGE = '最新の内容との差はありません';

export interface MasterHistoryDiffPanelProps {
  client: MasterHistoryClient;
  entityKey: string;
  version: string;
}

function describeDiffState(diff: MasterHistoryDiff): string | null {
  if (diff.is_latest) return DIFF_IS_LATEST_MESSAGE;
  if (diff.deleted) return DIFF_DELETED_MESSAGE;
  if (diff.changes.length === 0) return DIFF_NO_CHANGES_MESSAGE;
  return null;
}

export function MasterHistoryDiffPanel(props: MasterHistoryDiffPanelProps): React.JSX.Element {
  const { client, entityKey, version } = props;
  const load = useMemo(() => () => client.diff(entityKey, version), [client, entityKey, version]);
  const { data, loading, error, reload } = useMasterHistoryLoad<MasterHistoryDiff>(load);

  if (loading) {
    return (
      <p data-testid="master-history-diff-loading">
        <Spinner /> 最新の内容と比較中...
      </p>
    );
  }

  if (error !== null) {
    if (error instanceof ApiError && error.status === 404) {
      return (
        <p role="status" data-testid="master-history-diff-notfound">
          {DIFF_NOT_FOUND_MESSAGE}
        </p>
      );
    }
    return (
      <StatusBanner variant="error" data-testid="master-history-diff-error">
        <span>{describeApiErrorMessage(error)}</span>{' '}
        <button type="button" onClick={reload} data-testid="master-history-diff-retry">
          再読み込み
        </button>
      </StatusBanner>
    );
  }

  if (data === null) return <></>;

  const message = describeDiffState(data);
  if (message !== null) {
    return (
      <p role="status" data-testid="master-history-diff-message">
        {message}
      </p>
    );
  }

  return (
    <MasterHistoryChangeTable
      changes={data.changes}
      beforeHeading="この版"
      afterHeading="最新"
      testIdPrefix="master-history-diff"
    />
  );
}
